import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Who has visited the site, where they are, and anything that looked shady —
// wrong passwords, sign-up attempts, bots poking at addresses that don't exist.
// Tap an address to see everything it did, and block it if you want.

const RANGES = [
  { value: 1, label: '24h' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const SHADY_KINDS = ['login_failed', 'login_locked', 'signup_blocked', 'forbidden', 'probe', 'rate_limited', 'blocked'];

const ago = (iso) => {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const stamp = (iso) => new Date(iso).toLocaleString([], {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});

const placeName = (loc) => {
  if (!loc) return 'looking up location…';
  if (loc.country === 'private network') return 'private network';
  return [loc.city, loc.region, loc.country].filter(Boolean).join(', ') || 'unknown location';
};

// a readable device line from the browser's self-description
const deviceName = (ua) => {
  if (!ua) return '';
  const u = ua.toLowerCase();
  if (/bot|crawl|spider|slurp|curl|wget|python|go-http|scrapy|headless/.test(u)) return 'bot or script';
  const os = /iphone/.test(u) ? 'iPhone' : /ipad/.test(u) ? 'iPad' : /android/.test(u) ? 'Android'
    : /mac os x/.test(u) ? 'Mac' : /windows/.test(u) ? 'Windows' : /linux/.test(u) ? 'Linux' : '';
  const br = /edg\//.test(u) ? 'Edge' : /crios|chrome\//.test(u) ? 'Chrome' : /fxios|firefox/.test(u) ? 'Firefox'
    : /safari/.test(u) ? 'Safari' : '';
  return [br, os].filter(Boolean).join(' on ') || 'unknown device';
};

export default function SecurityLog() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [view, setView] = useState('all');            // all | flagged | blocked
  const [hideMine, setHideMine] = useState(true);
  const [openIp, setOpenIp] = useState('');
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [confirmBlockIp, setConfirmBlockIp] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [busyIp, setBusyIp] = useState('');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    load(days);
  }, [days]);

  const describeError = (err) => {
    if (err && err.response) {
      const d = err.response.data?.detail;
      return `${err.response.status} — ${typeof d === 'string' ? d : 'no detail'}`;
    }
    return (err && err.message) || 'network error';
  };

  const load = (range) => {
    setLoading(true);
    setErrorMsg('');
    axios.get(`${API}/admin/security/overview?token=${token}&days=${range}`)
      .then(res => setData(res.data))
      .catch(err => setErrorMsg(`couldn't load the security log: ${describeError(err)}`))
      .finally(() => setLoading(false));
  };

  const toggleIp = (ip) => {
    setConfirmBlockIp('');
    if (openIp === ip) { setOpenIp(''); return; }
    setOpenIp(ip);
    setEvents([]);
    setEventsLoading(true);
    axios.get(`${API}/admin/security/events?token=${token}&ip=${encodeURIComponent(ip)}`)
      .then(res => setEvents(res.data.events || []))
      .catch(err => setErrorMsg(`couldn't load that address: ${describeError(err)}`))
      .finally(() => setEventsLoading(false));
  };

  const block = (ip) => {
    setBusyIp(ip);
    setErrorMsg('');
    axios.post(`${API}/admin/security/block`, { token, ip, reason: blockReason })
      .then(() => { setConfirmBlockIp(''); setBlockReason(''); load(days); })
      .catch(err => setErrorMsg(`couldn't block ${ip}: ${describeError(err)}`))
      .finally(() => setBusyIp(''));
  };

  const unblock = (ip) => {
    setBusyIp(ip);
    setErrorMsg('');
    axios.delete(`${API}/admin/security/block?token=${token}&ip=${encodeURIComponent(ip)}`)
      .then(() => load(days))
      .catch(err => setErrorMsg(`couldn't unblock ${ip}: ${describeError(err)}`))
      .finally(() => setBusyIp(''));
  };

  const all = data?.ips || [];
  const listed = all
    .filter(r => !(hideMine && r.is_you && !r.blocked))
    .filter(r => view === 'all' || (view === 'flagged' ? r.suspicious > 0 && !r.is_you : r.blocked));
  const totals = data?.totals || {};

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '12px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', flexShrink: 0,
    },
    backBtn: {
      background: 'transparent', border: 'none', cursor: 'pointer', color: '#4a3d30',
      fontSize: '13px', fontFamily: "'DM Sans', sans-serif", flexShrink: 0, padding: 0,
    },
    title: {
      flex: 1, textAlign: 'center', fontFamily: "'Cormorant Garamond', serif",
      fontSize: '20px', fontStyle: 'italic',
    },
    rangeSelect: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px',
      padding: '6px 10px', color: '#c9935a', fontSize: '12px',
      fontFamily: "'DM Sans', sans-serif", outline: 'none',
    },
    body: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '22px 20px 80px', maxWidth: '720px', width: '100%',
      margin: '0 auto', boxSizing: 'border-box',
    },
    tiles: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
      gap: '8px', marginBottom: '18px',
    },
    tile: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)',
      borderRadius: '14px', padding: '12px 14px',
    },
    tileNum: (alert) => ({
      fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', lineHeight: 1.1,
      color: alert ? '#e0a0a0' : '#e8ddd0', fontVariantNumeric: 'tabular-nums',
    }),
    tileLabel: { fontSize: '11px', color: '#8a7466', marginTop: '2px' },
    hint: { fontSize: '11px', color: '#4a3d30', lineHeight: 1.6, marginBottom: '14px' },
    controls: {
      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px',
    },
    chip: (active) => ({
      padding: '6px 14px', borderRadius: '16px', fontSize: '12px', cursor: 'pointer',
      border: active ? 'none' : '1px solid rgba(201,147,90,0.15)',
      background: active ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent',
      color: active ? '#0e0b0f' : '#8a7466', fontFamily: "'DM Sans', sans-serif",
    }),
    toggle: {
      marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px',
      fontSize: '12px', color: '#8a7466', cursor: 'pointer',
    },
    errorBanner: {
      background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)',
      borderRadius: '12px', padding: '10px 14px', marginBottom: '14px',
      fontSize: '12px', color: '#e0a0a0', wordBreak: 'break-word',
    },
    card: (flagged, blocked) => ({
      background: '#1e1825', borderRadius: '14px', marginBottom: '8px',
      border: blocked ? '1px solid rgba(200,106,106,0.35)'
        : flagged ? '1px solid rgba(212,160,90,0.3)' : '1px solid rgba(201,147,90,0.06)',
      overflow: 'hidden',
    }),
    cardHead: { padding: '12px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '4px' },
    ipRow: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
    ip: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '13px',
      color: '#e8ddd0', wordBreak: 'break-all',
    },
    badge: (tone) => ({
      padding: '1px 8px', borderRadius: '10px', fontSize: '10.5px', whiteSpace: 'nowrap',
      background: tone === 'bad' ? 'rgba(200,106,106,0.16)' : tone === 'warn' ? 'rgba(212,160,90,0.16)'
        : tone === 'you' ? 'rgba(127,199,154,0.14)' : 'rgba(201,147,90,0.1)',
      color: tone === 'bad' ? '#e0a0a0' : tone === 'warn' ? '#e0b87a'
        : tone === 'you' ? '#9fd4b1' : '#c9a57a',
    }),
    place: { fontSize: '12.5px', color: '#c9b8a6' },
    meta: { fontSize: '11px', color: '#8a7466', lineHeight: 1.6 },
    detail: {
      borderTop: '1px solid rgba(201,147,90,0.08)', padding: '10px 14px 14px',
      display: 'flex', flexDirection: 'column', gap: '10px', background: '#17131c',
    },
    eventList: { display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '320px', overflowY: 'auto' },
    event: { display: 'flex', gap: '10px', fontSize: '12px', lineHeight: 1.5 },
    eventTime: { color: '#4a3d30', flexShrink: 0, width: '96px', fontVariantNumeric: 'tabular-nums' },
    eventText: (shady) => ({ color: shady ? '#e0b0a0' : '#c9b8a6', minWidth: 0, wordBreak: 'break-word' }),
    eventSub: { color: '#4a3d30' },
    row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
    input: {
      flex: 1, minWidth: '160px', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '8px', padding: '7px 10px', color: '#e8ddd0', fontSize: '12px',
      fontFamily: "'DM Sans', sans-serif", outline: 'none',
    },
    dangerBtn: {
      padding: '7px 14px', borderRadius: '16px', border: 'none', cursor: 'pointer',
      background: '#b04a4a', color: '#f4e6e6', fontSize: '12px', fontFamily: "'DM Sans', sans-serif",
    },
    btn: {
      padding: '7px 14px', borderRadius: '16px', border: '1px solid rgba(201,147,90,0.25)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif",
    },
    quietBtn: {
      padding: '7px 10px', border: 'none', background: 'transparent', color: '#4a3d30',
      fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    },
    empty: { textAlign: 'center', color: '#4a3d30', fontSize: '13px', padding: '28px 10px', lineHeight: 1.8 },
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      <header style={s.header}>
        <button style={s.backBtn} onClick={() => navigate('/settings')}>← back</button>
        <div style={s.title}>Security</div>
        <select style={s.rangeSelect} value={days} onChange={e => setDays(Number(e.target.value))}>
          {RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </header>

      <div style={s.body}>
        {errorMsg && <div style={s.errorBanner}>⚠ {errorMsg}</div>}

        <div style={s.tiles}>
          <div style={s.tile}>
            <div style={s.tileNum(false)}>{totals.visitors ?? '–'}</div>
            <div style={s.tileLabel}>visitors (not you)</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileNum(false)}>{totals.visits ?? '–'}</div>
            <div style={s.tileLabel}>times the site was opened</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileNum(totals.failed_logins > 0)}>{totals.failed_logins ?? '–'}</div>
            <div style={s.tileLabel}>wrong passwords</div>
          </div>
          <div style={s.tile}>
            <div style={s.tileNum(totals.flagged_ips > 0)}>{totals.flagged_ips ?? '–'}</div>
            <div style={s.tileLabel}>addresses flagged</div>
          </div>
        </div>

        <div style={s.hint}>
          locations are approximate (city level, from the internet provider). "data centre"
          means the visit came from a server, not a home or phone, which usually means a bot.
          {data?.your_ip && <> you're on <span style={{ color: '#9fd4b1' }}>{data.your_ip}</span> right now.</>}
        </div>

        <div style={s.controls}>
          <button style={s.chip(view === 'all')} onClick={() => setView('all')}>everyone</button>
          <button style={s.chip(view === 'flagged')} onClick={() => setView('flagged')}>
            flagged{totals.flagged_ips ? ` ${totals.flagged_ips}` : ''}
          </button>
          <button style={s.chip(view === 'blocked')} onClick={() => setView('blocked')}>
            blocked{totals.blocked ? ` ${totals.blocked}` : ''}
          </button>
          <label style={s.toggle}>
            <input
              type="checkbox"
              checked={hideMine}
              onChange={e => setHideMine(e.target.checked)}
              style={{ accentColor: '#c9935a' }}
            />
            hide my devices
          </label>
        </div>

        {loading && !data ? (
          <div style={s.empty}>loading…</div>
        ) : listed.length === 0 ? (
          <div style={s.empty}>
            {view === 'flagged' ? 'nothing shady in this period.'
              : view === 'blocked' ? 'no blocked addresses.'
              : 'no visitors in this period.'}
          </div>
        ) : (
          listed.map(r => {
            const loc = r.location;
            const flagged = r.suspicious > 0 && !r.is_you;
            const isThisDevice = data?.your_ip === r.ip;
            return (
              <div key={r.ip} style={s.card(flagged, r.blocked)}>
                <div style={s.cardHead} onClick={() => toggleIp(r.ip)}>
                  <div style={s.ipRow}>
                    <span style={s.ip}>{r.ip}</span>
                    {isThisDevice && <span style={s.badge('you')}>this device</span>}
                    {r.is_you && !isThisDevice && <span style={s.badge('you')}>you</span>}
                    {r.blocked && <span style={s.badge('bad')}>blocked</span>}
                    {flagged && !r.blocked && <span style={s.badge('warn')}>flagged</span>}
                    {loc?.is_hosting && <span style={s.badge()}>data centre</span>}
                    {loc?.is_proxy && <span style={s.badge()}>VPN / proxy</span>}
                  </div>
                  <div style={s.place}>
                    {placeName(loc)}{loc?.isp ? ` · ${loc.isp}` : ''}
                  </div>
                  <div style={s.meta}>
                    {r.last_seen ? `last seen ${ago(r.last_seen)}` : 'not seen in this period'}
                    {r.user_agent ? ` · ${deviceName(r.user_agent)}` : ''}
                    {r.visits > 0 && ` · opened the site ${r.visits}×`}
                    {r.failed_logins > 0 && ` · ${r.failed_logins} wrong password${r.failed_logins === 1 ? '' : 's'}`}
                    {r.counts?.signup_blocked > 0 && ' · tried to sign up'}
                    {r.counts?.probe > 0 && ` · ${r.counts.probe} address probe${r.counts.probe === 1 ? '' : 's'}`}
                    {r.emails_tried?.length > 0 && ` · emails tried: ${r.emails_tried.join(', ')}`}
                    {r.blocked && r.blocked_reason && ` · blocked: ${r.blocked_reason}`}
                  </div>
                </div>

                {openIp === r.ip && (
                  <div style={s.detail}>
                    {eventsLoading ? (
                      <div style={s.meta}>loading…</div>
                    ) : (
                      <div style={s.eventList}>
                        {events.length === 0 && <div style={s.meta}>nothing recorded for this address yet.</div>}
                        {events.map((e, i) => (
                          <div key={i} style={s.event}>
                            <span style={s.eventTime}>{stamp(e.at)}</span>
                            <span style={s.eventText(SHADY_KINDS.includes(e.kind))}>
                              {e.label}
                              {e.path && e.kind !== 'login_failed' && e.kind !== 'login_ok' && (
                                <span style={s.eventSub}> · {e.path}</span>
                              )}
                              {e.detail && <span style={s.eventSub}> · {e.detail}</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {r.user_agent && (
                      <div style={{ ...s.meta, wordBreak: 'break-word' }}>device: {r.user_agent}</div>
                    )}

                    {r.blocked ? (
                      <div style={s.row}>
                        <button style={s.btn} onClick={() => unblock(r.ip)} disabled={busyIp === r.ip}>
                          {busyIp === r.ip ? 'unblocking…' : 'unblock'}
                        </button>
                      </div>
                    ) : isThisDevice ? (
                      <div style={s.meta}>this is the address you're using now, so it can't be blocked.</div>
                    ) : confirmBlockIp === r.ip ? (
                      <div style={s.row}>
                        <input
                          style={s.input}
                          placeholder="reason (optional)"
                          value={blockReason}
                          onChange={e => setBlockReason(e.target.value)}
                        />
                        <button style={s.dangerBtn} onClick={() => block(r.ip)} disabled={busyIp === r.ip}>
                          {busyIp === r.ip ? 'blocking…' : 'block this address'}
                        </button>
                        <button style={s.quietBtn} onClick={() => setConfirmBlockIp('')}>cancel</button>
                      </div>
                    ) : (
                      <div style={s.row}>
                        <button style={s.btn} onClick={() => { setConfirmBlockIp(r.ip); setBlockReason(''); }}>
                          block…
                        </button>
                        {r.is_you && (
                          <span style={s.meta}>this is one of your devices. blocking it would lock that device out.</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
